import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Play, Pause, RotateCcw, GripHorizontal, Pencil, ExternalLink, Check, LogIn } from 'lucide-react';
import { persianNumber } from '../services/utils';

const GAP = 24;

interface PomodoroProps {
  mobileToolsActive?: boolean;
}

const Pomodoro: React.FC<PomodoroProps> = ({ mobileToolsActive = false }) => {
  // Timer State (Preserved across dock/undock)
  const [timeLeft, setTimeLeft] = useState(25 * 60);
  const [isActive, setIsActive] = useState(false);
  const [mode, setMode] = useState<'FOCUS' | 'BREAK'>('FOCUS');
  const [isEditing, setIsEditing] = useState(false);
  const [editMinutes, setEditMinutes] = useState('25');

  // UI State
  const [isDesktop, setIsDesktop] = useState(true);
  const [isFloating, setIsFloating] = useState(false); // Only relevant for desktop undocking
  const [position, setPosition] = useState({ x: GAP, y: window.innerHeight - 250 });
  const [isDragging, setIsDragging] = useState(false);
  
  const dragStartOffset = useRef({ x: 0, y: 0 });
  const widgetRef = useRef<HTMLDivElement>(null);
  
  // Slot Refs
  const [sidebarSlot, setSidebarSlot] = useState<HTMLElement | null>(null);
  const [mobileToolsSlot, setMobileToolsSlot] = useState<HTMLElement | null>(null);

  // Check Environment and Find Slots
  useEffect(() => {
    const checkLayout = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      
      // If switching to mobile, ensure not floating (dock into tools tab)
      if (!desktop) {
        setIsFloating(false);
      }
    };

    checkLayout();
    window.addEventListener('resize', checkLayout);
    
    // Find slots interval (in case of dynamic loading)
    const interval = setInterval(() => {
       setSidebarSlot(document.getElementById('sidebar-pomodoro-slot'));
       setMobileToolsSlot(document.getElementById('mobile-tools-pomodoro-slot'));
    }, 500);

    return () => {
      window.removeEventListener('resize', checkLayout);
      clearInterval(interval);
    };
  }, []);

  // Timer Interval
  useEffect(() => {
    let interval: any = null;
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsActive(false);
      if (mode === 'FOCUS') {
        setMode('BREAK');
        setTimeLeft(5 * 60);
      } else {
        setMode('FOCUS');
        setTimeLeft(25 * 60);
      }
    }
    return () => clearInterval(interval);
  }, [isActive, timeLeft, mode]);

  // Actions
  const toggleTimer = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsActive(!isActive);
  };
  
  const resetTimer = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsActive(false);
    setTimeLeft(mode === 'FOCUS' ? 25 * 60 : 5 * 60);
  };

  const handleSaveEdit = () => {
    const mins = parseInt(editMinutes);
    if (!isNaN(mins) && mins > 0) {
      setTimeLeft(mins * 60);
    }
    setIsEditing(false);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${persianNumber(mins.toString().padStart(2, '0'))}:${persianNumber(secs.toString().padStart(2, '0'))}`;
  };

  // Dragging Logic (Only for Desktop Floating Mode)
  useEffect(() => {
    if (!isFloating) return;

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        e.preventDefault();
        setPosition({ 
            x: e.clientX - dragStartOffset.current.x, 
            y: e.clientY - dragStartOffset.current.y 
        });
    };

    const handleMouseUp = (e: MouseEvent) => {
        if (!isDragging) return;
        setIsDragging(false);
    };

    if (isDragging) {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isFloating]);

  const startDrag = (e: React.MouseEvent) => {
    if (isEditing) return;
    setIsDragging(true);
    if (widgetRef.current) {
        const rect = widgetRef.current.getBoundingClientRect();
        dragStartOffset.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
    }
  };

  // --- RENDERING ---

  // Shared Content Component
  const TimerContent = ({ minimal = false, canFloat = false }) => (
    <div className={`
      bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 flex flex-col items-center gap-2 group transition-all w-full
      ${minimal ? 'p-3 shadow-none' : 'p-4 shadow-sm hover:shadow-md'}
    `}>
         {/* Header */}
         <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2 overflow-hidden">
               <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isActive ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`}></div>
               <span className="text-[10px] lg:text-xs font-medium text-slate-500 inline-block truncate">
                 {mode === 'FOCUS' ? 'تمرکز' : 'استراحت'}
               </span>
            </div>
            {/* Controls */}
            <div className={`flex items-center gap-1 ${minimal ? '' : 'opacity-100 lg:opacity-0 lg:group-hover:opacity-100'} transition-opacity`}>
                {!isEditing && (
                  <button 
                    onClick={() => {
                       setIsEditing(true);
                       setEditMinutes(Math.floor(timeLeft / 60).toString());
                       setIsActive(false);
                    }}
                    className="text-slate-400 hover:text-primary-500 p-1 rounded transition-colors"
                    title="ویرایش زمان"
                  >
                    <Pencil size={14} />
                  </button>
                )}
                {canFloat && (
                  <button 
                    onClick={() => {
                      setIsFloating(true);
                      setPosition({ x: GAP, y: window.innerHeight - 250 });
                    }}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1 rounded transition-colors"
                    title="شناور کردن"
                  >
                    <ExternalLink size={14} />
                  </button>
                )}
            </div>
         </div>
         
         {isEditing ? (
            <div className="flex items-center justify-center gap-1 w-full animate-in fade-in zoom-in duration-200">
               <input 
                  type="number" 
                  value={editMinutes}
                  onChange={(e) => setEditMinutes(e.target.value)}
                  className="w-full max-w-[80px] bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded text-center text-xl py-1 focus:ring-2 focus:ring-primary-500 outline-none shadow-sm"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                  placeholder="دقیقه"
                />
               <button 
                onClick={handleSaveEdit} 
                className="p-1.5 bg-green-50 dark:bg-green-900/30 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/50 rounded transition-colors"
               >
                 <Check size={20} />
               </button>
            </div>
         ) : (
            <div 
              className="text-4xl font-bold font-mono text-slate-800 dark:text-slate-100 cursor-pointer hover:text-primary-600 transition-colors select-none w-full text-center py-2" 
              dir="ltr"
              onClick={() => {
                setIsEditing(true);
                setEditMinutes(Math.floor(timeLeft / 60).toString());
                setIsActive(false);
              }}
              title="برای ویرایش کلیک کنید"
            >
              {formatTime(timeLeft)}
            </div>
         )}

         <div className="flex gap-2 w-full">
            <button 
              onClick={toggleTimer}
              disabled={isEditing}
              className={`flex-1 py-2 rounded-lg flex justify-center items-center transition-all text-white shadow-sm
                ${isActive ? 'bg-orange-500 hover:bg-orange-600 shadow-orange-200 dark:shadow-none' : 'bg-primary-600 hover:bg-primary-700 shadow-primary-200 dark:shadow-none'}
                ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
               {isActive ? <Pause size={20} /> : <Play size={20} />}
            </button>
            <button 
               onClick={resetTimer}
               disabled={isEditing}
               className="p-2 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
            >
               <RotateCcw size={20} />
            </button>
         </div>
    </div>
  );

  // 1. DESKTOP: Sidebar Docked
  if (isDesktop && !isFloating && sidebarSlot) {
    return createPortal(<TimerContent minimal={true} canFloat={true} />, sidebarSlot);
  }

  // 2. DESKTOP: Floating Widget
  if (isDesktop && isFloating) {
    return createPortal(
      <div 
        ref={widgetRef}
        onMouseDown={startDrag}
        style={{ left: position.x, top: position.y }}
        className={`fixed z-[100] w-[200px] cursor-move shadow-2xl rounded-2xl animate-in zoom-in-95 duration-200`}
      >
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-200 dark:border-slate-700 relative">
          <div className="absolute top-2 left-2 flex gap-1">
             <button onClick={() => setIsFloating(false)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-slate-400">
               <LogIn size={14} className="rtl:rotate-180" />
             </button>
             <div className="p-1 text-slate-300"><GripHorizontal size={14} /></div>
          </div>
          <div className="mt-4">
             <TimerContent minimal={false} canFloat={false} />
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // 3. MOBILE: Tools Tab Slot
  // Only render if we have the slot (implies Tools tab is open) AND we are in mobile mode
  if (!isDesktop && mobileToolsSlot && mobileToolsActive) {
    return createPortal(
      <div className="h-full flex flex-col justify-center">
         <TimerContent minimal={false} canFloat={false} />
      </div>, 
      mobileToolsSlot
    );
  }

  return null;
};

export default Pomodoro;